<script lang="ts" setup>
import { computed } from 'vue'
import { PaymentMethodEnum, formatDiscountLabel } from '../constants'
import paypalIcon from '@/assets/paypal.png'
import stripeIcon from '@/assets/stripe.png'
import paypalLightIcon from '@/assets/paypal-light.png'
import stripeLightIcon from '@/assets/stripe-light.png'

interface PaymentMethod {
  value: number
  label: string
  icon: string
  /** 暗色主题（/pay 商店页深色按钮）下使用的白色图标 */
  darkIcon?: string
  originalMethod?: any
}

const props = withDefaults(defineProps<{
  modelValue?: any
  availableMethods: any[]
  variant?: 'default' | 'large'
  dark?: boolean
}>(), {
  modelValue: undefined,
  variant: 'default',
  dark: false,
})

defineEmits<{
  (e: 'update:model-value', value: number): void
}>()

// 所有支付方式配置
const allPaymentMethods: PaymentMethod[] = [
  {
    value: PaymentMethodEnum.PAYPAL,
    label: 'PayPal',
    icon: paypalIcon,
    darkIcon: paypalLightIcon,
  },
  {
    value: PaymentMethodEnum.STRIPE,
    label: 'Stripe',
    icon: stripeIcon,
    darkIcon: stripeLightIcon,
  },
]

// 根据 availableMethods 过滤出可用的支付方式，并保持接口返回的顺序
const paymentMethods = computed<PaymentMethod[]>(() => {
  // 确保是数组后再进行操作
  const methods = props.availableMethods || []

  // 按照接口返回的顺序构建支付方式列表
  return methods
    .map((m) => {
      const methodConfig = allPaymentMethods.find(config => Number(config.value) === Number(m.paymentMethod))
      return methodConfig ? { ...methodConfig, originalMethod: m } : null
    })
    .filter(Boolean) as PaymentMethod[]
})
</script>

<template>
  <!-- Default 样式：小的圆形 pill 样式（用于弹窗） -->
  <div v-if="variant === 'default'" class="payment-method-selector">
    <div
      v-for="method in paymentMethods"
      :key="method.value"
      class="payment-method-btn"
      :class="{ active: Number(props.modelValue) === Number(method.value) }"
      @click="$emit('update:model-value', method.value)"
    >
      <div class="py-6px px-32px box-border rounded-999px" :class="{ 'bg-white': Number(props.modelValue) === Number(method.value) }">
        <img :src="method.icon" :alt="method.label" class="payment-icon pop">
      </div>
    </div>
  </div>

  <!-- Large 样式：大按钮样式（用于 pay 和 subscribe 页面） -->
  <div v-else class="payment-method-selector-large" :class="{ dark: props.dark }">
    <div
      v-for="method in paymentMethods"
      :key="method.value"
      class="payment-method-btn-large"
      :class="{
        active: Number(props.modelValue) === Number(method.value),
        paypal: method.value === PaymentMethodEnum.PAYPAL && Number(props.modelValue) === Number(method.value),
      }"
      @click="$emit('update:model-value', method.value)"
    >
      <img :src="props.dark && method.darkIcon ? method.darkIcon : method.icon" :alt="method.label" class="payment-icon-large">
      <div v-if="formatDiscountLabel(method?.originalMethod?.maxDiscountRate)" class="discount-badge-large">
        {{ formatDiscountLabel(method?.originalMethod?.maxDiscountRate) }}
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Default 样式 */
.payment-method-selector {
  display: flex;
  padding: 4px;
  background: #f1f5f9;
  border-radius: 999px;
  width: fit-content;
  margin: 0 auto 8px auto;
  min-width: 200px;
  margin-top: 16px;
}

.payment-method-btn {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 999px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: 600;
  border: none;
  background: transparent;
}

.payment-method-btn.active {
  background: #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.payment-icon {
  height: 20px;
  object-fit: contain;
}
.payment-icon.pop {
  height: 24px;
  min-width: 60px;
  display: block;
}

/* Large 样式 */
.payment-method-selector-large {
  display: flex;
  gap: 12px;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  padding: 0;
  width: 100%;
  /* background-color: #fff; */
  margin: 0 10px;
  padding: 20px 0;
  box-sizing: border-box;
}

.payment-method-btn-large {
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 24px;
  width: 136px;
  flex: none;
  height: 34px;
  background: #fff;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-sizing: border-box;
}

.payment-method-btn-large.active {
  background: #F6F2FFFF;
  border-color: #9875FBFF;
}

.payment-method-btn-large.active.paypal {
  background: #F6F2FFFF;
  border-color: #9875FBFF;
}

.payment-icon-large {
  height: 20px;
  object-fit: contain;
}

/* Large 样式折扣标签 */
.discount-badge-large {
  position: absolute;
  top: -8px;
  left: -3px;
  right: auto;
  background: linear-gradient(135deg, #F961BCFF 0%, #F74984FF 100%);
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  padding: 0px 6px;
  border-radius: 80px 100px 100px 0px;
  white-space: nowrap;
  line-height: 1.3;
  z-index: 10;
  min-width: auto;
  text-align: center;
  transform: scale(0.95);
}

/* Dark 暗色主题样式（/pay 商店页：深色按钮 + 白色图标，匹配设计稿） */
.payment-method-selector-large.dark .payment-method-btn-large {
  width: 116px;
  height: 32px;
  padding: 8px 0;
  background: rgba(28, 27, 33, 1);
  border-color: rgba(165, 165, 165, 1);
}

.payment-method-selector-large.dark .payment-icon-large {
  height: 16px;
}

.payment-method-selector-large.dark .payment-method-btn-large.active,
.payment-method-selector-large.dark .payment-method-btn-large.active.paypal {
  /* 中间保持黑色，仅外层一圈渐变 border */
  background:
    linear-gradient(rgba(28, 27, 33, 1), rgba(28, 27, 33, 1)) padding-box,
    linear-gradient(135deg, #AAEDFF 0%, #FF7CCC 50%, #FFC69B 100%) border-box;
  border: 1px solid transparent;
}
</style>
